// MARK://dhk8/admin-shell/pages/modules/PriceListDetailPage
// Ficha "ver-primero" de una lista de precios (barrido a ficha de detalle, Gustavo
// 2026-06-23). Header con nombre + #overrides + pill activa/inactiva + KPI (precios
// especiales) + Tabs (General, Precios, Historial). El tab General se ve primero;
// "Editar" abre el SectionedSheet (nombre + activa) → renamePriceList +
// setPriceListActive. El tab Precios reubica el editor de overrides (producto +
// precio) que vivía inline en la lista del catálogo, cableando setPriceListItem /
// removePriceListItem. No hay getPriceList(id) singular → se carga listPriceLists()
// y se hace find(id), igual que ProductDetailPage resuelve la receta por la lista.
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil, Trash2 } from 'lucide-react'

import * as cat from '../../lib/clients/catalog.js'
import type { PriceList, Product } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn, parseMxnToCents } from '../../lib/money.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import { SummaryStrip } from '../../components/StatCard.js'
import Tabs from '../../components/Tabs.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import AuditTrail from '../../components/AuditTrail.js'
import { useToast } from '../../components/Toast.js'
import { useConfirm } from '../../components/ConfirmDialog.js'

type Tab = 'general' | 'precios' | 'historial'

export default function PriceListDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  // No hay getPriceList(id): se carga la lista y se hace find (como ProductDetailPage
  // con recetas). listProducts da los nombres para los overrides.
  const pls = useAsync((s) => cat.listPriceLists(s))
  const prods = useAsync((s) => cat.listProducts(s))

  const [tab, setTab] = useState<Tab>('general')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<{ name: string; active: boolean } | null>(null)
  const [busy, setBusy] = useState(false)

  const pl = useMemo(() => (pls.data ?? []).find((x) => x.id === id) ?? null, [pls.data, id])
  const overrideCount = pl ? Object.keys(pl.overrides).length : 0

  function openEdit() { if (pl) { setForm({ name: pl.name, active: pl.active }); setEditing(true) } }
  async function save() {
    if (!form || !pl) return
    setBusy(true)
    try {
      // General edita nombre (renamePriceList) y estado (setPriceListActive). No
      // existe un update combinado en el client → se aplican los cambios que
      // difieren del valor actual.
      if (form.name.trim() !== pl.name) await cat.renamePriceList(pl.id, form.name.trim())
      if (form.active !== pl.active) await cat.setPriceListActive(pl.id, form.active)
      toast('Lista actualizada', 'success'); setEditing(false); pls.reload()
    } catch (e) { toast(e instanceof Error ? e.message : 'No se pudo guardar', 'danger') } finally { setBusy(false) }
  }

  const kpis = useMemo(() => {
    if (!pl) return []
    return [
      { label: 'Precios especiales', value: overrideCount },
    ]
  }, [pl, overrideCount])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/catalog')} aria-label="Volver al catálogo"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Lista de precios</h1><p className="adm-page__sub">Catálogo · ficha de la lista de precios</p></div>
        </div>
      </div>

      {asyncState(pls.loading, pls.error, pls.reload)}
      {!pls.loading && !pl && <div className="adm-empty">Lista de precios no encontrada.</div>}

      {pl && (
        <EntityDetailPage
          title={pl.name}
          subtitle={`${overrideCount} ${overrideCount === 1 ? 'precio especial' : 'precios especiales'}`}
          pills={<StatusPill tone={lifecycleTone(pl.active)} label={pl.active ? 'Activa' : 'Inactiva'} />}
          kpis={<SummaryStrip items={kpis} />}
          actions={<button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openEdit}><Pencil size={15} /> Editar</button>}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'precios', label: 'Precios', count: overrideCount },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones de la lista de precios"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Nombre">{pl.name}</Field>
              <Field label="Estado">{pl.active ? 'Activa' : 'Inactiva'}</Field>
              <Field label="Precios especiales">{overrideCount}</Field>
            </div>
          )}

          {tab === 'precios' && (
            <PriceListItemsEditor pl={pl} products={prods.data ?? []} productsLoading={prods.loading} onChanged={pls.reload} />
          )}

          {tab === 'historial' && <AuditTrail entityId={pl.id} />}
        </EntityDetailPage>
      )}

      {form && pl && (
        <SectionedSheet
          open={editing}
          title="Editar lista de precios"
          onClose={() => setEditing(false)}
          footer={<>
            <button className="adm-btn" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={save} disabled={busy || !form.name.trim()}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </>}
        >
          <FormSection title="Identidad">
            <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Happy hour / Mayoreo…" /></label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Activa</label>
          </FormSection>
        </SectionedSheet>
      )}
    </div>
  )
}

// Editor de overrides de una lista (reubicado de CatalogPage): muestra los precios
// especiales actuales y permite agregar/actualizar (upsert) o quitar el precio de un
// producto. Cablea setPriceListItem / removePriceListItem por el grafo.
function PriceListItemsEditor({ pl, products, productsLoading, onChanged }: { pl: PriceList; products: Product[]; productsLoading: boolean; onChanged: () => void }) {
  const { toast } = useToast()
  const confirm = useConfirm()
  const [productId, setProductId] = useState('')
  const [price, setPrice] = useState('')
  const [busy, setBusy] = useState(false)
  const pName = (pid: string) => products.find((p) => p.id === pid)?.name ?? pid

  async function addOverride() {
    if (!productId) { toast('Selecciona un producto', 'warn'); return }
    setBusy(true)
    try { await cat.setPriceListItem(pl.id, productId, parseMxnToCents(price)); toast('Precio guardado', 'success'); setProductId(''); setPrice(''); onChanged() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo guardar el precio', 'danger') } finally { setBusy(false) }
  }
  async function removeOverride(pid: string) {
    const ok = await confirm({ title: '¿Quitar precio especial?', message: `Se quitará el precio de "${pName(pid)}" en esta lista.`, tone: 'danger', confirmLabel: 'Quitar' })
    if (!ok) return
    try { await cat.removePriceListItem(pl.id, pid); toast('Precio quitado', 'success'); onChanged() }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo quitar', 'danger') }
  }

  const entries = Object.entries(pl.overrides)
  return (
    <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 10 }}>
      {productsLoading && entries.length === 0 && <p className="adm-row__meta">Cargando productos…</p>}
      {entries.length === 0 && <p className="adm-row__meta">Sin precios especiales. Agrega el precio de un producto en esta lista.</p>}
      {entries.map(([pid, cents]) => (
        <div key={pid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
          <span>{pName(pid)}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="adm-mono">{mxn(cents)}</span>
            <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => removeOverride(pid)} aria-label="Quitar precio"><Trash2 size={13} /></button>
          </span>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="adm-field" style={{ flex: 2, minWidth: 180 }}>
          <span className="adm-field__label">Producto</span>
          <select className="adm-select" value={productId} onChange={(e) => setProductId(e.target.value)}>
            <option value="">— Selecciona producto —</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label className="adm-field" style={{ flex: 1, minWidth: 120 }}>
          <span className="adm-field__label">Precio (MXN)</span>
          <input className="adm-input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" />
        </label>
        <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={addOverride} disabled={busy || !productId}>{busy ? 'Guardando…' : 'Guardar precio'}</button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}
